import React, { useState, useEffect } from 'react';
import Sidebar from "../components/Sidebar";
import Buttons from '../components/Button';
import Loading from '../components/Loading';
import { Category } from '../interfaces/product';
import {
  fetchCategoriesAndSubcategories,
  addCategory,
  deleteCategory,
  updateCategory,
  addSubcategory
} from '../graphql/api/categoryService';
import AlertMessage from '../components/AlertMessage';
// import IconPicker from '../components/IconPicker';
import IconPickerSelect from '../components/IconPickerSelect';

import './category.css';
import { availableIcons } from '../utils/icons';

const Categories = () => {
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [newCategory, setNewCategory] = useState('');
  const [newCategoryIcon, setNewCategoryIcon] = useState<string | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [tempCategoryName, setTempCategoryName] = useState('');
  const [tempCategoryIcon, setTempCategoryIcon] = useState<string | null>(null);
  const [newSubcategoryName, setNewSubcategoryName] = useState('');
  const [newSubcategoryIcon, setNewSubcategoryIcon] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<"success" | "error">("success");

  const iconMap = new Map<string, React.ComponentType<any>>(
    availableIcons.map(icon => [icon.name, icon.component as React.ComponentType<any>])
  );

  const renderIcon = (iconName?: string | null) => {
    if (!iconName) return <span className="category_icon_display">📂</span>;
    const Icon = iconMap.get(iconName);
    return Icon ? <Icon className="category_icon_display" /> : <span className="category_icon_display">📂</span>;
  };

  useEffect(() => {
    if (alertMessage) {
      const timer = setTimeout(() => {
        setAlertMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [alertMessage]);

  const mainCategories = allCategories.filter(cat => !cat.parentCategoryId);
  const subcategories = allCategories.filter(cat => cat.parentCategoryId);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const { categories } = await fetchCategoriesAndSubcategories();
        setAllCategories(categories);
        const firstMain = categories.find((c: Category) => !c.parentCategoryId) || null;
        setSelectedCategory(firstMain);
        setAlertMessage("Categories loaded successfully!");
        setAlertType("success");
      } catch (error) {
        console.error("Error fetching data!", error);
        setAlertMessage("Failed to load categories");
        setAlertType("error");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;
    setIsLoading(true);
    try {
      const category = await addCategory({
        name: newCategory,
        icon: newCategoryIcon || undefined
      });
      setAllCategories([...allCategories, category]);
      setNewCategory('');
      setNewCategoryIcon(null);
      if (!selectedCategory) setSelectedCategory(category);
      setAlertMessage("Category added successfully!");
      setAlertType("success");
    } catch (error) {
      console.error("Error adding category", error);
      setAlertMessage("Failed to add category");
      setAlertType("error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddSubcategory = async () => {
    if (!selectedCategory || !newSubcategoryName.trim()) return;
    setIsLoading(true);
    try {
      const subcategory = await addSubcategory({
        name: newSubcategoryName,
        parentCategoryId: selectedCategory.id,
        icon: newSubcategoryIcon || undefined
      });
      setAllCategories([...allCategories, subcategory]);
      setNewSubcategoryName('');
      setNewSubcategoryIcon(null);
      setAlertMessage("Subcategory added successfully!");
      setAlertType("success");
    } catch (error) {
      console.error("Error adding subcategory", error);
      setAlertMessage("Failed to add subcategory");
      setAlertType("error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    setIsLoading(true);
    try {
      await deleteCategory(categoryId);
      const updatedCategories = allCategories.filter(
        cat => cat.id !== categoryId && cat.parentCategoryId !== categoryId
      );
      setAllCategories(updatedCategories);
      if (selectedCategory?.id === categoryId) {
        const newSelected = updatedCategories.find((c: Category) => !c.parentCategoryId) || null;
        setSelectedCategory(newSelected);
      }
      setAlertMessage("Category deleted successfully!");
      setAlertType("success");
    } catch (error) {
      console.error("Error deleting category", error);
      setAlertMessage("Failed to delete category");
      setAlertType("error");
    } finally {
      setIsLoading(false);
    }
  };

  const startEditCategory = (category: Category) => {
    setEditingCategoryId(category.id);
    setTempCategoryName(category.name);
    setTempCategoryIcon(category.icon || null);
  };

  const cancelEdit = () => {
    setEditingCategoryId(null);
  };

  const saveCategoryEdit = async () => {
    if (!editingCategoryId || !tempCategoryName.trim()) return;
    setIsLoading(true);
    try {
      await updateCategory(editingCategoryId, {
        name: tempCategoryName,
        icon: tempCategoryIcon || undefined
      });
      setAllCategories(allCategories.map(cat =>
        cat.id === editingCategoryId
          ? { ...cat, name: tempCategoryName, icon: tempCategoryIcon || undefined }
          : cat
      ));
      if (selectedCategory?.id === editingCategoryId) {
        setSelectedCategory({
          ...selectedCategory,
          name: tempCategoryName,
          icon: tempCategoryIcon || undefined
        });
      }
      cancelEdit();
      setAlertMessage("Category updated successfully!");
      setAlertType("success");
    } catch (error) {
      console.error("Error updating category", error);
      setAlertMessage("Failed to update category");
      setAlertType("error");
    } finally {
      setIsLoading(false);
    }
  };

  const currentSubcategories = subcategories.filter(
    sub => sub.parentCategoryId === selectedCategory?.id
  );

  return (
    <div style={{ display: "flex" }}>
      <Sidebar isOpen={isSidebarOpen} setIsOpen={setIsSidebarOpen} />
      <div className="category_content" style={{ marginLeft: isSidebarOpen ? "250px" : "60px" }}>
       
        <div className="category_container">
           <br />
        <br />
        <br />
          <h2 className="category_heading_management">Category Management</h2>

          {alertMessage && <AlertMessage message={alertMessage} type={alertType} />}

          <div className="category_form">
            <input
              type="text"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              placeholder="New Category Name"
              className="category_form_input"
            />
            <div className="category_icon_picker_wrapper">
              <IconPickerSelect value={newCategoryIcon} onChange={setNewCategoryIcon} />
            </div>
            <Buttons text="Add Category" variant="add" onClick={handleAddCategory} className="category_add_button" />
          </div>

          <div className="category_layout">
            <div className="category_main_column">
              <h3 className="category_subheading">Main Categories</h3>
              <div className="category_list">
                {mainCategories.map(category => (
                  <div
                    key={category.id}
                    className={`category_item ${selectedCategory?.id === category.id ? 'category_item_selected' : ''}`}
                    onClick={() => setSelectedCategory(category)}
                  >
                    {editingCategoryId === category.id ? (
                      <div className="category_edit_container">
                        <input
                          type="text"
                          value={tempCategoryName}
                          onChange={(e) => setTempCategoryName(e.target.value)}
                          className="category_edit_input"
                        />
                        <div className="category_icon_picker_wrapper">
                          <IconPickerSelect value={tempCategoryIcon} onChange={setTempCategoryIcon} />
                        </div>
                        <div className="category_action_buttons">
                          <Buttons text="Save" variant="update" onClick={saveCategoryEdit} />
                          <Buttons text="Cancel" variant="cancel" onClick={cancelEdit} />
                        </div>
                      </div>
                    ) : (
                      <div className="category_content">
                        <div className="category_info">
                          <div className="category_icon">
                            {renderIcon(category.icon)}
                          </div>
                          <div className="category_text">
                            <span className="category_name">Name: {category.name}</span>
                            <span className="category_slug">Slug: {category.slug}</span>
                          </div>
                        </div>
                        <div className="category_actions">
                          <Buttons text="Edit" variant="edit" onClick={(e) => {
                            e.stopPropagation();
                            startEditCategory(category);
                          }} />
                          <Buttons text="Delete" variant="delete" onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCategory(category.id);
                          }} />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="category_subcategory_column">
              {selectedCategory ? (
                <>
                  <div className="category_subcategory_header">
                    <h3 className="category_subheading">
                      Subcategories for: <span className="category_selected">{selectedCategory.name}</span>
                    </h3>
                    <div className="category_selected_icon">
                      {renderIcon(selectedCategory.icon)}
                    </div>
                  </div>

                  <div className="category_form">
                    <input
                      type="text"
                      value={newSubcategoryName}
                      onChange={(e) => setNewSubcategoryName(e.target.value)}
                      placeholder="New Subcategory Name"
                      className="category_form_input"
                    />
                    <div className="category_icon_picker_wrapper">
                      <IconPickerSelect value={newSubcategoryIcon} onChange={setNewSubcategoryIcon} />
                    </div>
                    <Buttons
                      text="Add Subcategory"
                      variant="add"
                      onClick={handleAddSubcategory}
                      className="category_add_button"
                    />
                  </div>

                  <div className="category_list">
                    {currentSubcategories.map(subcategory => (
                      <div key={subcategory.id} className="category_item">
                        {editingCategoryId === subcategory.id ? (
                          <div className="category_edit_container">
                            <input
                              type="text"
                              value={tempCategoryName}
                              onChange={(e) => setTempCategoryName(e.target.value)}
                              className="category_edit_input"
                            />
                            <div className="category_icon_picker_wrapper">
                              <IconPickerSelect value={tempCategoryIcon} onChange={setTempCategoryIcon} />
                            </div>
                            <div className="category_action_buttons">
                              <Buttons text="Save" variant="update" onClick={saveCategoryEdit} />
                              <Buttons text="Cancel" variant="cancel" onClick={cancelEdit} />
                            </div>
                          </div>
                        ) : (
                          <div className="category_content">
                            <div className="category_info">
                              <div className="category_icon">
                                {renderIcon(subcategory.icon)}
                              </div>
                              <div className="category_text">
                                <span className="category_name">{subcategory.name}</span>
                              </div>
                            </div>
                            <div className="category_actions">
                              <Buttons text="Edit" variant="edit" onClick={() => startEditCategory(subcategory)} />
                              <Buttons text="Delete" variant="delete" onClick={() => handleDeleteCategory(subcategory.id)} />
                            </div>
                          </div>
                        )}
                      </div>
                    ))}

                    {currentSubcategories.length === 0 && (
                      <div className="category_empty">No subcategories found</div>
                    )}
                  </div>
                </>
              ) : (
                <div className="category_empty_state">
                  <p>Select a category to manage its subcategories</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {isLoading && <Loading />}
    </div>
  );
};

export default Categories;
